import { Injectable } from '@angular/core';
import {
  buildMediaVariantUrl,
  MediaImageProfile,
  shouldUseMediaVariants,
} from '../utils/media-image-profile.util';
import { resolveMediaUrl } from '../utils/resolve-media-url.util';

interface PreloadCandidate {
  src: string;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class MediaImageCacheService {
  private static readonly MAX_CONCURRENT_WARMS = 4;

  private readonly resolved = new Map<string, string>();
  private readonly inflight = new Map<string, Promise<string>>();
  private readonly warmQueue: Array<{ raw: string; profile: MediaImageProfile }> = [];
  private activeWarms = 0;

  private cacheKey(raw: string, profile: MediaImageProfile): string {
    return `${raw.trim()}::${profile}`;
  }

  getBestSrc(raw?: string | null, profile: MediaImageProfile = 'preview'): string {
    const key = (raw ?? '').trim();
    if (!key) {
      return '';
    }

    const cached = this.resolved.get(this.cacheKey(key, profile));
    if (cached) {
      return cached;
    }

    if (profile === 'full') {
      return resolveMediaUrl(key);
    }

    const variant = buildMediaVariantUrl(key, profile);
    if (variant) {
      return variant;
    }

    return resolveMediaUrl(key);
  }

  warm(raw?: string | null, profile: MediaImageProfile = 'preview'): void {
    const key = (raw ?? '').trim();
    if (!key || this.resolved.has(this.cacheKey(key, profile)) || this.inflight.has(this.cacheKey(key, profile))) {
      return;
    }
    if (this.warmQueue.some((item) => item.raw === key && item.profile === profile)) {
      return;
    }
    this.warmQueue.push({ raw: key, profile });
    this.pumpWarmQueue();
  }

  warmAll(urls: Array<string | null | undefined>, profile: MediaImageProfile = 'thumb'): void {
    for (const url of urls) {
      this.warm(url, profile);
    }
  }

  preload(raw: string, profile: MediaImageProfile = 'preview'): Promise<string> {
    const key = raw.trim();
    if (!key) {
      return Promise.resolve('');
    }

    const cacheId = this.cacheKey(key, profile);
    const cached = this.resolved.get(cacheId);
    if (cached) {
      return Promise.resolve(cached);
    }

    const pending = this.inflight.get(cacheId);
    if (pending) {
      return pending;
    }

    const promise = this.probeBestSrc(key, profile);
    this.inflight.set(cacheId, promise);
    return promise;
  }

  private pumpWarmQueue(): void {
    while (this.activeWarms < MediaImageCacheService.MAX_CONCURRENT_WARMS && this.warmQueue.length) {
      const next = this.warmQueue.shift();
      if (!next) {
        break;
      }
      if (this.resolved.has(this.cacheKey(next.raw, next.profile))) {
        continue;
      }
      this.activeWarms += 1;
      void this.preload(next.raw, next.profile).finally(() => {
        this.activeWarms = Math.max(0, this.activeWarms - 1);
        this.pumpWarmQueue();
      });
    }
  }

  private buildCandidates(raw: string, profile: MediaImageProfile): PreloadCandidate[] {
    const candidates: PreloadCandidate[] = [];

    if (profile !== 'full') {
      const variant = buildMediaVariantUrl(raw, profile);
      if (variant) {
        candidates.push({ src: variant, label: 'variant' });
      }
    }

    return candidates;
  }

  private probeBestSrc(raw: string, profile: MediaImageProfile): Promise<string> {
    const cacheId = this.cacheKey(raw, profile);
    const candidates = this.buildCandidates(raw, profile);

    return new Promise((resolve) => {
      const finish = (src: string) => {
        this.resolved.set(cacheId, src);
        this.inflight.delete(cacheId);
        resolve(src);
      };

      const tryAt = (index: number) => {
        const candidate = candidates[index];
        if (!candidate) {
          finish(resolveMediaUrl(raw));
          return;
        }

        // SSR / Node has no Image constructor — use first candidate URL.
        if (typeof Image === 'undefined') {
          finish(candidate.src);
          return;
        }

        const probe = new Image();
        probe.decoding = 'async';
        probe.onload = () => finish(candidate.src);
        probe.onerror = () => tryAt(index + 1);
        probe.src = candidate.src;
      };

      tryAt(0);
    });
  }

  shouldUseVariants(raw?: string | null): boolean {
    return shouldUseMediaVariants(raw);
  }
}
